/**
 * Onglet « Traces » (API v2 `/traces`) : chaque appel IA, son statut et son
 * issue.
 *
 * - Tuiles servies par le serveur, indépendantes des filtres de liste (contrat
 *   §5) : plus aucun taux calculé côté front (F-FE-04). `started` (en cours ou
 *   interrompu) est une tuile à part, jamais un échec.
 * - Filtres issus du référentiel renvoyé par le serveur (toutes les opérations
 *   et tous les statuts, MON-10-B).
 * - « Actualiser » recharge aussi cet onglet (`refreshToken`, F-FE-03), et ses
 *   filtres survivent au changement d'onglet.
 */
import React, { useMemo, useRef, useState } from 'react';
import { Button, Card, Col, Input, Row, Select, Space, Switch, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table/interface';
import { FlagOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
    AI_SOURCES,
    TRACE_STATUSES,
    type AiSource,
    type TraceRow,
    type TracesFilters,
    type TracesParams,
    type TracesResponse,
} from '../../../api/monitoring/v2';
import { INK, STATE_COLORS, cardStyle } from '../monitoringTheme';
import StatTile from '../shared/StatTile';
import CompactValue from '../shared/CompactValue';
import EmptyState from '../shared/EmptyState';
import ExportButton from '../shared/ExportButton';
import QueryStatusView from '../shared/QueryStatusView';
import MetaBanner from '../shared/MetaBanner';
import { useMonitoringQuery } from '../shared/useMonitoringQuery';
import { useMonitoringApiRef } from '../shared/useScopes';
import { useServerTable } from '../shared/useServerTable';
import { toCommonParams } from '../shared/useMonitoringFilters';
import { useEnumLabel } from '../shared/i18nCatalog';
import TraceDrawer from './TraceDrawer';
import DataTable from '../shared/DataTable';
import { STATUS_COLOR } from '../shared/traceStatus';
import type { MonitoringTabProps } from './PedagogyTab';

const { Text } = Typography;


const TracesTab: React.FC<MonitoringTabProps> = ({ filters, ready, active, refreshToken }) => {
    const { t } = useTranslation();
    const enumLabel = useEnumLabel();
    const apiRef = useMonitoringApiRef();

    const [listFilters, setListFilters] = useState<TracesFilters>({});
    const [questDraft, setQuestDraft] = useState('');
    const [openCallId, setOpenCallId] = useState<string | null>(null);

    const common = toCommonParams(filters);
    const group = JSON.stringify(['traces', common, listFilters]);
    const table = useServerTable(group);
    const params: TracesParams = { ...common, ...listFilters, list: table.query };

    const query = useMonitoringQuery<TracesResponse>(
        JSON.stringify(['traces', params]),
        (signal) => apiRef.current.getTraces(params, signal),
        { refreshToken, enabled: active && ready, groupKey: group }
    );
    const data = query.data;

    const patchFilters = (patch: Partial<TracesFilters>) =>
        setListFilters((prev) => {
            const next = { ...prev, ...patch };
            (Object.keys(next) as Array<keyof TracesFilters>).forEach((k) => {
                if (next[k] === undefined || next[k] === '' || next[k] === false) delete next[k];
            });
            return next;
        });

    // Le référentiel survit à une réponse en erreur : les filtres restent utilisables.
    const referentialRef = useRef<TracesResponse['referential'] | null>(null);
    if (data) referentialRef.current = data.referential;
    const referential = data?.referential ?? referentialRef.current;
    const operationOptions = useMemo(
        () =>
            (referential?.operations ?? [])
                .filter((op) => !listFilters.source || op.source === listFilters.source)
                .map((op) => ({ value: op.code, label: enumLabel('ai_operation', op.code) })),
        [referential, listFilters.source, enumLabel]
    );

    const traceFiltersWithoutSource = { ...listFilters, source: undefined };

    // Liste rendue depuis les colonnes typées du serveur (§5, §10.6) ; seuls le
    // statut (étiquette colorée) et les signalements ont un rendu propre.
    const cellOverrides: Record<string, (value: unknown, row: TraceRow) => React.ReactNode> = {
        status: (value) => (
            <Tag color={STATUS_COLOR[String(value)] ?? STATE_COLORS.neutral} style={{ borderRadius: 10 }}>
                {enumLabel('ai_status', String(value))}
            </Tag>
        ),
        report_count: (value) =>
            value ? (
                <Tag color={STATE_COLORS.critical} icon={<FlagOutlined />}>
                    <CompactValue value={value as number} />
                </Tag>
            ) : null,
    };

    const actionColumns: ColumnsType<TraceRow> = [
        {
            key: 'actions',
            width: 90,
            render: (_, row) => (
                <Button size="small" onClick={() => setOpenCallId(row.call_id)}>
                    {t('monitoring.traces.open')}
                </Button>
            ),
        },
    ];

    return (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <QueryStatusView query={query} tiles={4} />

            {data && (
                <>
                    <MetaBanner meta={data.meta} />
                    <EmptyState reasons={data.empty_reasons} />

                    <Row gutter={[12, 12]}>
                        <Col xs={24} lg={12}>
                            <StatTile
                                metricId="TR-01"
                                value={
                                    <Space size={[6, 6]} wrap>
                                        {TRACE_STATUSES.filter((s) => s !== 'started').map((status) => (
                                            <Tag
                                                key={status}
                                                color={STATUS_COLOR[status]}
                                                style={{ borderRadius: 10, fontSize: 12 }}
                                                data-status={status}
                                            >
                                                {enumLabel('ai_status', status)} : <CompactValue value={data.tiles.by_status[status]} />
                                            </Tag>
                                        ))}
                                    </Space>
                                }
                            />
                        </Col>
                        <Col xs={12} lg={4}>
                            <StatTile metricId="TR-STARTED" value={<CompactValue value={data.tiles.by_status.started} />} />
                        </Col>
                        <Col xs={12} lg={4}>
                            <StatTile metricId="TR-02" icon={<FlagOutlined />} value={<CompactValue value={data.tiles.reports} />} />
                        </Col>
                        <Col xs={24} lg={4}>
                            <StatTile
                                metricId="TR-OUTCOME"
                                value={
                                    <Space size={[6, 6]} wrap>
                                        {Object.entries(data.tiles.by_outcome).map(([outcome, n]) => (
                                            <Tag key={outcome} bordered={false} style={{ fontSize: 12 }}>
                                                {enumLabel('ai_outcome', outcome)} : <CompactValue value={n} />
                                            </Tag>
                                        ))}
                                    </Space>
                                }
                            />
                        </Col>
                    </Row>
                </>
            )}

            {/* Les filtres de liste restent affichés même en erreur (400 sur un
                filtre, 403 de périmètre) : on doit pouvoir en sortir. */}
            {query.status !== 'role_required' && (
                <Card
                    size="small"
                    style={cardStyle}
                    title={
                        <Space size={8} wrap>
                            <Select
                                allowClear
                                style={{ minWidth: 150 }}
                                aria-label={t('monitoring.traces.filters.source')}
                                placeholder={t('monitoring.traces.filters.source')}
                                value={listFilters.source}
                                onChange={(v?: AiSource) => patchFilters({ source: v, operation: undefined })}
                                options={AI_SOURCES.map((s) => ({ value: s, label: enumLabel('ai_source', s) }))}
                            />
                            <Select
                                allowClear
                                showSearch
                                optionFilterProp="label"
                                style={{ minWidth: 200 }}
                                aria-label={t('monitoring.traces.filters.operation')}
                                placeholder={t('monitoring.traces.filters.operation')}
                                value={listFilters.operation}
                                onChange={(v?: string) => patchFilters({ operation: v })}
                                options={operationOptions}
                            />
                            <Select
                                allowClear
                                style={{ minWidth: 160 }}
                                aria-label={t('monitoring.traces.filters.status')}
                                placeholder={t('monitoring.traces.filters.status')}
                                value={listFilters.status}
                                onChange={(v?: string) => patchFilters({ status: v })}
                                options={(referential?.statuses ?? []).map((s) => ({ value: s, label: enumLabel('ai_status', s) }))}
                            />
                            <Select
                                allowClear
                                style={{ minWidth: 170 }}
                                aria-label={t('monitoring.traces.filters.outcome')}
                                placeholder={t('monitoring.traces.filters.outcome')}
                                value={listFilters.outcome}
                                onChange={(v?: string) => patchFilters({ outcome: v })}
                                options={(referential?.outcomes ?? []).map((s) => ({ value: s, label: enumLabel('ai_outcome', s) }))}
                            />
                            <Input.Search
                                allowClear
                                size="middle"
                                style={{ width: 230 }}
                                aria-label={t('monitoring.traces.filters.questId')}
                                placeholder={t('monitoring.traces.filters.questId')}
                                value={questDraft}
                                onChange={(e) => setQuestDraft(e.target.value)}
                                onSearch={(v) => patchFilters({ quest_id: v.trim() || undefined })}
                            />
                            <Space size={6}>
                                <Switch
                                    size="small"
                                    checked={!!listFilters.flagged}
                                    aria-label={t('monitoring.traces.filters.flagged')}
                                    onChange={(v) => patchFilters({ flagged: v })}
                                />
                                <Text style={{ fontSize: 12, color: INK.secondary }}>{t('monitoring.traces.filters.flagged')}</Text>
                            </Space>
                        </Space>
                    }
                    extra={
                        <ExportButton
                            dataset="traces"
                            params={{ ...common, source: listFilters.source, traceFilters: traceFiltersWithoutSource, table: table.filters }}
                        />
                    }
                >
                    <DataTable<TraceRow>
                        table={data?.list ?? null}
                        rowKey={(row) => row.call_id}
                        loading={query.status === 'refreshing' || query.status === 'loading'}
                        server={{ state: table.state, onChange: table.setState }}
                        cellOverrides={cellOverrides}
                        extraColumns={actionColumns}
                        emptyText={t('monitoring.traces.empty')}
                    />
                </Card>
            )}

            <TraceDrawer callId={openCallId} onClose={() => setOpenCallId(null)} />
        </Space>
    );
};

export default TracesTab;
